import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import {
	ToneOfVoiceCapturePayload,
	ToneOfVoiceChannel,
	ToneOfVoiceMessageType,
} from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import {
	createDefaultDraft,
	Draft,
	DraftRecipient,
	DraftSender,
	timingFromDays,
} from "./inboxDraft.types";
import { resolveInboxReplySubject } from "./resolveInboxReplySubject.util";

/**
 * Reply-agent draft slots (reply1/reply2/reply3) → tone-of-voice message type. Mirrors the backend
 * slot-index derivation documented in the tone-of-voice feedback-loop brief.
 */
const REPLY_SLOT_MESSAGE_TYPES: ToneOfVoiceMessageType[] = [
	ToneOfVoiceMessageType.REPLY_INITIAL,
	ToneOfVoiceMessageType.REPLY_FOLLOW_UP,
	ToneOfVoiceMessageType.REPLY_NURTURING,
];

export interface ReplyDraftEntry {
	content: string | null | undefined;
	days: number | null | undefined;
	/** Reply Agent's reasoning for this slot — surfaced as the inbox "Why this?" hover (PREC-3617). */
	reasoning?: string | null;
}

const toToneChannel = (type: DraftSender["type"] | undefined): ToneOfVoiceChannel =>
	type === "email" ? ToneOfVoiceChannel.EMAIL : ToneOfVoiceChannel.LINKEDIN;

/**
 * Tone-of-voice snapshot for a prefilled reply draft. Attaching it lets the send path
 * (`draftsToSendMessageItems` → `POST /schedule/send-messages`) capture the diff between the AI
 * reply draft and the final sent text — the same self-learning loop Chat uses (PREC-3454).
 */
const buildReplyAiOrigin = (
	content: string,
	slotIndex: number,
	channelType: DraftSender["type"] | undefined
): ToneOfVoiceCapturePayload => ({
	originalText: content,
	messageType: REPLY_SLOT_MESSAGE_TYPES[slotIndex] ?? ToneOfVoiceMessageType.REPLY_NURTURING,
	channel: toToneChannel(channelType),
});

/**
 * Build the prefilled inbox drafts from a lead's reply-agent slots (reply1/2/3). Each draft carries
 * an `aiOrigin` snapshot so an edited-then-sent reply feeds the tone-of-voice learning loop.
 * Follow-up timings are nudged to land at least a day after the previous draft.
 */
export const buildReplyPrefillDrafts = (
	entries: ReplyDraftEntry[],
	defaultFrom: DraftSender | undefined,
	defaultTo: DraftRecipient | undefined,
	conversations: Conversation[] = []
): Draft[] => {
	const filteredEntries = entries.filter((entry) => !!entry.content);
	const subject = resolveInboxReplySubject(conversations, defaultFrom?.type);

	return filteredEntries.map((entry, idx, arr) => {
		let timing = timingFromDays(entry.days);

		// For non-first drafts, ensure timing is at least 1 day after the previous draft
		if (idx > 0) {
			const prevDays = timingFromDays(arr[idx - 1].days).scheduledInDays ?? 0;
			const currentDays = timing.scheduledInDays ?? 0;

			if (currentDays <= prevDays) {
				timing = timingFromDays(prevDays + 1);
			}
		}

		const draft = createDefaultDraft(idx + 1, defaultFrom, defaultTo, entry.content ?? "", timing);

		if (subject) {
			draft.subject = subject;
		}

		draft.aiOrigin = buildReplyAiOrigin(entry.content ?? "", idx, defaultFrom?.type);

		if (entry.reasoning) {
			draft.reasoning = entry.reasoning;
		}

		return draft;
	});
};
